import { useCallback, useEffect, useState } from "react";
import {
  type AdminAgentReport,
  type AgentReportType,
  fetchAgentReports,
} from "../../api/admin-agents";
import {
  Pagination,
  Td,
  Th,
  fmtDate,
  tableStyles,
} from "../../components/DataTable";
import { t } from "../../i18n";

const PAGE_SIZE = 20;
const REPORT_TYPE_OPTIONS = [
  "credential_phishing",
  "system_impersonation",
  "spam",
  "unauthorized_collection",
  "other",
] as const satisfies ReadonlyArray<AgentReportType>;

type ReportTypeFilter = AgentReportType | "all";

type ReportFilters = {
  readonly agentId: string;
  readonly reportType: ReportTypeFilter;
};

const DEFAULT_FILTERS: ReportFilters = { agentId: "", reportType: "all" };

export function AgentReportsPanel(): React.JSX.Element {
  const [draftFilters, setDraftFilters] =
    useState<ReportFilters>(DEFAULT_FILTERS);
  const [filters, setFilters] = useState<ReportFilters>(DEFAULT_FILTERS);
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<AdminAgentReport[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const agentId = filters.agentId.trim();
      const res = await fetchAgentReports({
        agent_id: agentId === "" ? undefined : agentId,
        report_type:
          filters.reportType === "all" ? undefined : filters.reportType,
        limit: PAGE_SIZE,
        offset,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("agent_reports.failed"));
    } finally {
      setLoading(false);
    }
  }, [filters, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyFilters = useCallback((): void => {
    setOffset(0);
    setFilters(draftFilters);
  }, [draftFilters]);

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <section>
      <ReportFilterBar
        filters={draftFilters}
        onChange={setDraftFilters}
        onApply={applyFilters}
        onRefresh={() => void load()}
      />
      <p style={s.summary}>
        {t("agent_reports.total", { total: total.toLocaleString() })}
      </p>
      {error && <p style={s.error}>{error}</p>}
      {loading ? (
        <p style={s.loadingText}>{t("agents.loading")}</p>
      ) : (
        <>
          <ReportsTable reports={items} />
          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={(p) => setOffset((p - 1) * PAGE_SIZE)}
          />
        </>
      )}
    </section>
  );
}

function ReportFilterBar({
  filters,
  onChange,
  onApply,
  onRefresh,
}: {
  filters: ReportFilters;
  onChange: (filters: ReportFilters) => void;
  onApply: () => void;
  onRefresh: () => void;
}): React.JSX.Element {
  return (
    <form
      style={s.filterRow}
      onSubmit={(e) => {
        e.preventDefault();
        onApply();
      }}
    >
      <input
        type="text"
        placeholder={t("agent_reports.agent_id_placeholder")}
        value={filters.agentId}
        onChange={(e) => onChange({ ...filters, agentId: e.target.value })}
        style={s.searchInput}
      />
      <select
        value={filters.reportType}
        onChange={(e) =>
          onChange({
            ...filters,
            reportType: e.target.value as ReportTypeFilter,
          })
        }
        style={s.filterSelect}
        aria-label={t("agent_reports.type_filter")}
      >
        <option value="all">{t("agent_reports.type_all")}</option>
        {REPORT_TYPE_OPTIONS.map((type) => (
          <option key={type} value={type}>
            {reportTypeLabel(type)}
          </option>
        ))}
      </select>
      <button type="submit" style={s.filterBtn}>
        {t("common.filter")}
      </button>
      <button type="button" onClick={onRefresh} style={s.secondaryBtn}>
        {t("common.refresh")}
      </button>
    </form>
  );
}

function ReportsTable({
  reports,
}: {
  reports: ReadonlyArray<AdminAgentReport>;
}): React.JSX.Element {
  if (reports.length === 0) {
    return <p style={s.loadingText}>{t("agent_reports.empty")}</p>;
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={s.table}>
        <thead>
          <tr>
            <Th>{t("agent_reports.col_report")}</Th>
            <Th>{t("agent_reports.col_agent")}</Th>
            <Th>{t("agent_reports.col_reporter")}</Th>
            <Th>{t("agent_reports.col_type")}</Th>
            <Th>{t("agent_reports.col_message")}</Th>
            <Th>{t("agent_reports.col_description")}</Th>
          </tr>
        </thead>
        <tbody>
          {reports.map((report) => (
            <ReportRow key={report.id} report={report} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReportRow({
  report,
}: {
  report: AdminAgentReport;
}): React.JSX.Element {
  return (
    <tr>
      <Td>
        <IdBlock id={report.id} />
        <span style={s.metaLine}>{fmtDate(report.created_at)}</span>
      </Td>
      <Td>
        <NamedId name={report.agent_name} id={report.agent_template_id} />
      </Td>
      <Td>
        <NamedId name={report.reporter_name} id={report.reporter_id} />
      </Td>
      <Td>{reportTypeLabel(report.report_type)}</Td>
      <Td>
        {report.message_id ? (
          <IdBlock id={report.message_id} />
        ) : (
          t("agent_reports.empty_value")
        )}
      </Td>
      <Td>
        {truncate(report.description ?? t("agent_reports.empty_value"), 120)}
      </Td>
    </tr>
  );
}

function NamedId({
  name,
  id,
}: {
  name: string;
  id: string;
}): React.JSX.Element {
  return (
    <span>
      <span>{name || t("agent_reports.unknown_name")}</span>
      <IdBlock id={id} />
    </span>
  );
}

function IdBlock({ id }: { id: string }): React.JSX.Element {
  return (
    <span title={id} style={s.idLine}>
      {id}
    </span>
  );
}

function reportTypeLabel(type: AgentReportType): string {
  return t(`agent_reports.type.${type}`);
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}...` : value;
}

const s: Record<string, React.CSSProperties> = {
  ...tableStyles,
  summary: {
    fontSize: 13,
    color: "var(--color-text-secondary)",
    margin: "0 0 12px",
  },
  searchInput: {
    flex: "1 1 260px",
    minWidth: 220,
    padding: "6px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 13,
  },
  filterSelect: {
    padding: "6px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    fontSize: 13,
    background: "var(--color-bg)",
  },
  secondaryBtn: {
    padding: "6px 14px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
  },
  idLine: {
    display: "block",
    maxWidth: 280,
    overflowWrap: "anywhere",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--color-text-secondary)",
  },
  metaLine: {
    display: "block",
    marginTop: 4,
    fontSize: 12,
    color: "var(--color-text-secondary)",
  },
};
